/**
 * /downhole/rates — the downhole rate card (fieldiq-api#906, dh_rates 0452).
 *
 * WHAT A RATE IS, IN THE API'S TERMS. One row prices ONE key — a component
 * category, or one specific tool — for one customer, or for everybody when
 * `customer_id` is null (the company default), over a range of calendar days
 * (both ends inclusive; no end date is open-ended). It quotes any of six
 * figures: run, hourly, day, footage, minimum charge, lost-in-hole. A figure
 * left out is NOT QUOTED — not $0 — and a run whose billing needs it cannot be
 * priced (the invoice refuses it, RUNS_UNPRICED). So the register prints
 * "Not quoted", never a zero and never a dash that reads as missing data.
 *
 * WHICH RATE A RUN USES is the API's (priceRun): the first live row effective
 * on the run day, in the order tool + customer → category + customer → tool
 * default → category default. The screen never re-derives it; the run log's
 * price panel shows the API's answer.
 *
 * Two live rows for one key may not overlap in time — the database refuses it
 * (409 RATE_OVERLAP, naming the row in the way), and the dialog says which.
 * Delete is soft and reversible; a restore can overlap too.
 *
 * FOOTAGE is stored per foot. A metric tenant reads and types it per metre
 * (lib/downhole-units.ts, footage rate helpers).
 *
 * ROLES: read admin, office_staff, dispatcher; write admin, office_staff
 * (lib/downhole-roles.ts). A dispatcher sees the card and no write.
 *
 * No stat strip: the band's circle clearance was measured against Quotes' and
 * Rate Sheets' stats only, and nothing here is a figure the API returns.
 *
 * PERSONNEL RATES (api#1057) are a second tab, `?tab=personnel`, shown only
 * while the company's Directional & MWD mode is on (every personnel-rate door
 * answers 409 DD_MODE_OFF otherwise). The strip sits in the body under the
 * band and switches which question the page answers, so it is tabs, not chips
 * (CLAUDE.md, fe#704 / fe#712), and carries no count.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArchiveRestore, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { api, getApiErrorMessage } from '../../api/client';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useCreateRate, useDeleteRate, useDownholeComponents, useDownholeRates, useRestoreRate, useUpdateRate } from '../../hooks/useDownhole';
import { useDownholeFeature } from '../../hooks/useDownholeSettings';
import { unwrapArray } from '../../lib/api-envelope';
import { formatCalendarDate } from '../../lib/calendar-date';
import { fmtCurrency } from '../../lib/display-labels';
import { DH_CATEGORY_LABEL, dhLabel } from '../../lib/downhole-labels';
import { onlineOrSay } from '../../lib/downhole-offline';
import { canReadDownholeRates, canWriteDownholeRates } from '../../lib/downhole-roles';
import { footageRateUnit, formatStandbyPct, standbyPctFromInput, standbyPctToInput, toDisplayFootageRate, toStoredFootageRate } from '../../lib/downhole-units';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { DH_COMPONENT_CATEGORIES, type DhRate, type DhRateWrite, type DhUnitSystem } from '../../types/downhole';
import { DH_DATE_INPUT, DH_INPUT, DH_SECONDARY, DhCategoryPill, DhDialog, DhEmpty, DhField, DhPanel } from './DownholeBits';
import { PersonnelRatesTab } from './PersonnelRates';

const TABS = [
  { key: 'tools', label: 'Tool rates' },
  { key: 'personnel', label: 'Personnel rates' },
] as const;

const PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50';
const DANGER =
  'inline-flex h-9 items-center gap-1.5 rounded-fi-control border border-black/20 bg-fi-signal-red px-3 text-sm font-bold text-fi-signal-ink disabled:opacity-50';

/** The six figures a rate can quote, in the API's names. */
const MONEY_FIELDS = ['run_rate', 'hourly_rate', 'day_rate', 'footage_rate', 'minimum_charge', 'lih_rate'] as const;
type MoneyField = (typeof MONEY_FIELDS)[number];

const moneyLabel = (k: MoneyField, units: DhUnitSystem | null): string =>
  ({
    run_rate: 'Per run',
    hourly_rate: 'Per hour',
    day_rate: 'Per day',
    footage_rate: `Per ${footageRateUnit(units)} drilled`,
    minimum_charge: 'Minimum charge',
    lih_rate: 'Lost in hole',
  })[k];

/** A stored figure in the tenant's terms (footage per m for metric), or null. */
const shown = (k: MoneyField, v: number | null, units: DhUnitSystem | null) => (k === 'footage_rate' ? toDisplayFootageRate(v, units) : v);

function useCustomers() {
  return useQuery({
    queryKey: ['customers'],
    queryFn: async () => unwrapArray<{ id: string; name: string }>((await api.customers.list()).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** "Not quoted" is a fact about the rate, so it is said, not dashed. */
function Money({ value, currency, digits = 2 }: { value: number | null; currency: string; digits?: number }) {
  if (value === null || value === undefined) return <span className="text-xs text-fieldiq-muted">Not quoted</span>;
  return <>{fmtCurrency(value, currency, digits)}</>;
}

/**
 * api#1062 daily-billing prices (Directional & MWD), shown and written only
 * while the mode is on. ABSENT (a read from before batch 4) is unknown and
 * prints "—"; null is a fact, "Not quoted".
 */
const DAILY_MONEY_FIELDS = ['standby_day_rate', 'mobilization_charge', 'demobilization_charge'] as const;
const DAILY_LABEL: Record<(typeof DAILY_MONEY_FIELDS)[number], string> = {
  standby_day_rate: 'Standby day',
  mobilization_charge: 'Mobilization',
  demobilization_charge: 'Demobilization',
};

function DailyMoney({ value, currency }: { value: number | null | undefined; currency: string }) {
  if (value === undefined) return <>—</>;
  return <Money value={value} currency={currency} />;
}

/** Standby is a day rate OR a share of the day rate: whichever the rate carries. */
function StandbyCell({ r }: { r: DhRate }) {
  if (r.standby_day_rate === undefined && r.standby_pct === undefined) return <>—</>;
  if (r.standby_day_rate != null) return <>{fmtCurrency(r.standby_day_rate, r.currency, 2)}</>;
  if (r.standby_pct != null) return <>{formatStandbyPct(r.standby_pct)} of day</>;
  return <span className="text-xs text-fieldiq-muted">Not quoted</span>;
}

const dayRange = (r: DhRate) =>
  `${formatCalendarDate(r.effective_date) ?? '—'} – ${r.expiry_date ? formatCalendarDate(r.expiry_date) ?? '—' : 'onward'}`;

export default function RatesScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canRead = canReadDownholeRates(user?.role);
  const canWrite = canWriteDownholeRates(user?.role);
  const [customer, setCustomer] = useState('');
  const [category, setCategory] = useState('');
  const [on, setOn] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [editing, setEditing] = useState<DhRate | 'new' | null>(null);
  const [deleting, setDeleting] = useState<DhRate | null>(null);
  const [highlight, setHighlight] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const ddMwd = useDownholeFeature('dd_mwd');
  const tab = ddMwd && searchParams.get('tab') === 'personnel' ? 'personnel' : 'tools';
  const pickTab = (key: string) => {
    const next = new URLSearchParams(searchParams);
    if (key === 'tools') next.delete('tab');
    else next.set('tab', key);
    setSearchParams(next, { replace: true });
  };

  // customer, category and the day go to the API. "Company default" is a
  // selection (customer_id null) the route has no filter for, so it is made
  // over the answer.
  const params = useMemo(
    () => ({
      ...(customer && customer !== 'default' && { customer_id: customer }),
      ...(category && { category }),
      ...(/^\d{4}-\d{2}-\d{2}$/.test(on) && { on }),
      ...(showDeleted && { include_deleted: 'true' as const }),
    }),
    [customer, category, on, showDeleted],
  );
  const query = useDownholeRates(params, canRead);
  const customers = useCustomers();
  const restore = useRestoreRate();
  const failure = queryLoadFailure(query);
  const units = query.data?.meta.unit_system ?? null;
  const tenantCurrency = query.data?.meta.currency ?? null;
  const today = query.data?.meta.today ?? null;
  const rows = (query.data?.data ?? []).filter((r) => customer !== 'default' || r.customer_id === null);
  const filtered = !!(customer || category || on);

  const doRestore = async (r: DhRate) => {
    if (!onlineOrSay(toast.error)) return;
    try {
      await restore.mutateAsync(r.id);
      toast.success('Rate restored');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'The rate was not restored.'));
      const id = (error as { response?: { data?: { conflicting_rate_id?: string } } })?.response?.data?.conflicting_rate_id;
      if (id) setHighlight(id);
    }
  };

  if (!canRead) {
    return (
      <div className="min-h-full bg-fieldiq-dark">
        <PageHeaderBand eyebrow="Downhole tools" title="Tool Rates" onBack={() => navigate(-1)} backLabel="Back" />
        <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)]">
          <div className="mx-auto max-w-[1920px] rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
            <DhEmpty>Rates are visible to admin, office staff and dispatch.</DhEmpty>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="downhole-rates-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Downhole tools"
        title={tab === 'personnel' ? 'Personnel Rates' : 'Tool Rates'}
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canWrite && query.data && tab === 'tools' ? (
            <button type="button" className="fi-action fi-action--standard fi-action--primary" onClick={() => setEditing('new')}>
              <Plus className="size-4" aria-hidden="true" />
              Add rate
            </button>
          ) : undefined
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {ddMwd && (
            <div role="tablist" aria-label="Rate cards" className="flex gap-5 overflow-x-auto border-b border-fieldiq-border">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.key}
                  onClick={() => pickTab(t.key)}
                  className={`whitespace-nowrap border-b-2 px-1 pb-2 text-sm transition ${
                    tab === t.key ? 'border-fi-amber font-extrabold text-fieldiq-text' : 'border-transparent font-bold text-fieldiq-muted hover:text-fieldiq-text'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
          {tab === 'personnel' ? (
            <PersonnelRatesTab
              canWrite={canWrite}
              customers={customers.data ?? []}
              tenantCurrency={tenantCurrency}
              today={today}
            />
          ) : (
          <>
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-64">
              <DhField label="Customer">
                <select className={DH_INPUT} value={customer} onChange={(e) => setCustomer(e.target.value)}>
                  <option value="">Every customer and the default</option>
                  <option value="default">Company default only</option>
                  {(customers.data ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </DhField>
            </div>
            <div className="w-56">
              <DhField label="Category">
                <select className={DH_INPUT} value={category} onChange={(e) => setCategory(e.target.value)}>
                  <option value="">All categories</option>
                  {DH_COMPONENT_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {DH_CATEGORY_LABEL[c]}
                    </option>
                  ))}
                </select>
              </DhField>
            </div>
            <div className="w-44">
              <DhField label="In effect on">
                <input type="date" className={DH_DATE_INPUT} value={on} onChange={(e) => setOn(e.target.value)} />
              </DhField>
            </div>
            <label className="flex h-10 items-center gap-2 text-sm text-fieldiq-text">
              <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />
              Show deleted
            </label>
          </div>

          <DhPanel
            eyebrow="Rate card"
            title="Rates"
            testId="downhole-rate-register"
            footer={
              query.data && !failure && rows.length > 0
                ? `${rows.length} ${rows.length === 1 ? 'rate' : 'rates'}${filtered ? ' match these filters' : ''}. A run uses the most specific rate in effect on its run-in day.`
                : undefined
            }
          >
            {query.isLoading || (!failure && !query.data) ? (
              <div className="flex justify-center py-16">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading rates" />
              </div>
            ) : failure ? (
              <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="rates" />
            ) : rows.length === 0 ? (
              <DhEmpty>
                {filtered ? 'No rate matches these filters.' : 'There are no rates yet. Runs cannot be invoiced until their tools have a rate.'}
              </DhEmpty>
            ) : (
              <div className="overflow-x-auto">
                <table className={`w-full text-left ${ddMwd ? 'min-w-[1500px]' : 'min-w-[1180px]'}`}>
                  <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                    <tr className="border-l-4 border-l-transparent">
                      <th scope="col" className="px-4 py-3">Applies to</th>
                      <th scope="col" className="px-4 py-3">Customer</th>
                      {MONEY_FIELDS.map((k) => (
                        <th key={k} scope="col" className="px-4 py-3 text-right">
                          {moneyLabel(k, units)}
                        </th>
                      ))}
                      {ddMwd && (
                        <>
                          <th scope="col" className="px-4 py-3 text-right">Standby</th>
                          <th scope="col" className="px-4 py-3 text-right">{DAILY_LABEL.mobilization_charge}</th>
                          <th scope="col" className="px-4 py-3 text-right">{DAILY_LABEL.demobilization_charge}</th>
                        </>
                      )}
                      <th scope="col" className="px-4 py-3">In effect</th>
                      {canWrite && <th scope="col" className="px-4 py-3 text-right">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr
                        key={r.id}
                        data-rate-id={r.id}
                        className={`border-t border-l-4 border-fieldiq-border border-l-transparent ${highlight === r.id ? 'bg-fi-amber/10' : ''} ${
                          r.deleted_at ? 'opacity-60' : ''
                        }`}
                      >
                        <td className="px-4 py-3">
                          {r.component_id ? (
                            <span className="font-mono text-sm font-bold text-white">{r.component_serial || '—'}</span>
                          ) : (
                            <DhCategoryPill category={r.category} />
                          )}
                          {r.deleted_at && (
                            <span className="ml-2 rounded-fi-pill border border-black/20 bg-fi-signal-slate px-2 py-0.5 text-[0.62rem] font-black uppercase text-fi-signal-ink">
                              Deleted
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-fieldiq-text">
                          {r.customer_id ? r.customer_name || '—' : <span className="text-fieldiq-muted">Company default</span>}
                        </td>
                        {MONEY_FIELDS.map((k) => (
                          <td key={k} className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm text-fieldiq-text">
                            <Money value={shown(k, r[k], units)} currency={r.currency} digits={k === 'footage_rate' ? 4 : 2} />
                          </td>
                        ))}
                        {ddMwd && (
                          <>
                            <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm text-fieldiq-text" data-testid={`standby-${r.id}`}>
                              <StandbyCell r={r} />
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm text-fieldiq-text">
                              <DailyMoney value={r.mobilization_charge} currency={r.currency} />
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm text-fieldiq-text">
                              <DailyMoney value={r.demobilization_charge} currency={r.currency} />
                            </td>
                          </>
                        )}
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">
                          {dayRange(r)}
                          <span className="ml-1 text-xs text-fieldiq-muted">{r.currency}</span>
                        </td>
                        {canWrite && (
                          <td className="whitespace-nowrap px-4 py-3 text-right">
                            {r.deleted_at ? (
                              <button type="button" className={DH_SECONDARY} disabled={restore.isPending} onClick={() => doRestore(r)}>
                                <ArchiveRestore className="size-4" aria-hidden="true" />
                                Restore
                              </button>
                            ) : (
                              <span className="inline-flex gap-2">
                                <button type="button" className={DH_SECONDARY} onClick={() => setEditing(r)} aria-label={`Edit rate for ${r.component_serial || dhLabel(DH_CATEGORY_LABEL, r.category)}`}>
                                  <Pencil className="size-4" aria-hidden="true" />
                                  Edit
                                </button>
                                <button type="button" className={DH_SECONDARY} onClick={() => setDeleting(r)} aria-label={`Delete rate for ${r.component_serial || dhLabel(DH_CATEGORY_LABEL, r.category)}`}>
                                  <Trash2 className="size-4" aria-hidden="true" />
                                </button>
                              </span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </DhPanel>
          </>
          )}
        </div>
      </section>

      {canWrite && editing && (
        <RateFormDialog
          rate={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          units={units}
          tenantCurrency={tenantCurrency}
          today={today}
          customers={customers.data ?? []}
          daily={ddMwd}
          onConflict={(id) => {
            setHighlight(id);
            setShowDeleted(false);
          }}
        />
      )}
      {canWrite && deleting && <DeleteRateDialog rate={deleting} onClose={() => setDeleting(null)} />}
    </div>
  );
}

type Values = Record<string, string>;

function valuesOf(r: DhRate | null, units: DhUnitSystem | null, tenantCurrency: string | null, today: string | null): Values {
  const v: Values = {
    scope: r?.component_id ? 'component' : 'category',
    category: r?.category ?? '',
    component_id: r?.component_id ?? '',
    customer_id: r?.customer_id ?? '',
    currency: r?.currency ?? (tenantCurrency === 'USD' || tenantCurrency === 'CAD' ? tenantCurrency : 'CAD'),
    effective_date: r?.effective_date ?? today ?? '',
    expiry_date: r?.expiry_date ?? '',
    notes: r?.notes ?? '',
  };
  for (const k of MONEY_FIELDS) {
    const n = r ? shown(k, r[k], units) : null;
    v[k] = n === null || n === undefined ? '' : String(k === 'footage_rate' ? Math.round(n * 10000) / 10000 : n);
  }
  for (const k of DAILY_MONEY_FIELDS) {
    const n = r?.[k];
    v[k] = n === null || n === undefined ? '' : String(n);
  }
  v.standby_pct = standbyPctToInput(r?.standby_pct);
  return v;
}

const MONEY = /^\d+(\.\d+)?$/;
const DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Add or edit a rate. An edit sends only what changed (a metric footage rate
 * opened and saved untouched is not a round trip). A 409 RATE_OVERLAP names
 * the row in the way; the register highlights it.
 */
function RateFormDialog({
  rate,
  onClose,
  units,
  tenantCurrency,
  today,
  customers,
  daily,
  onConflict,
}: {
  rate: DhRate | null;
  onClose: () => void;
  units: DhUnitSystem | null;
  tenantCurrency: string | null;
  today: string | null;
  customers: { id: string; name: string }[];
  /** The dd_mwd mode is on: offer and send the daily-billing prices. */
  daily: boolean;
  onConflict: (id: string) => void;
}) {
  const create = useCreateRate();
  const update = useUpdateRate();
  const editing = !!rate;
  const initial = useMemo(() => valuesOf(rate, units, tenantCurrency, today), [rate, units, tenantCurrency, today]);
  const [v, setV] = useState<Values>(initial);
  const [toolSearch, setToolSearch] = useState('');
  const tools = useDownholeComponents(toolSearch.trim() ? { q: toolSearch.trim() } : undefined);
  const busy = create.isPending || update.isPending;
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setV((all) => ({ ...all, [k]: e.target.value }));

  const submit = async () => {
    const problems: string[] = [];
    if (v.scope === 'category' && !v.category) problems.push('Choose the category this rate prices');
    if (v.scope === 'component' && !v.component_id) problems.push('Choose the tool this rate prices');
    if (!DAY.test(v.effective_date)) problems.push('Choose the day the rate starts');
    if (v.expiry_date && !DAY.test(v.expiry_date)) problems.push('The end day must be a date');
    if (v.expiry_date && v.effective_date && v.expiry_date < v.effective_date) problems.push('The end day cannot be before the start day');
    for (const k of MONEY_FIELDS) if (v[k].trim() && !MONEY.test(v[k].trim())) problems.push(`${moneyLabel(k, units)} must be an amount without separators`);
    if (MONEY_FIELDS.every((k) => !v[k].trim())) problems.push('Quote at least one figure');
    for (const k of DAILY_MONEY_FIELDS) if (v[k].trim() && !MONEY.test(v[k].trim())) problems.push(`${DAILY_LABEL[k]} must be an amount without separators`);
    if (standbyPctFromInput(v.standby_pct) === undefined) problems.push('Standby % must be a percent above 0 and at most 100');
    if (v.standby_day_rate.trim() && v.standby_pct.trim()) problems.push('A rate has a standby day rate or a standby %, not both. Clear one');
    if (problems.length) {
      toast.error(problems.join('. '));
      return;
    }

    const body: DhRateWrite = {};
    const changed = (k: string) => !editing || v[k] !== initial[k];
    const scopeChanged = changed('scope') || changed('category') || changed('component_id');
    if (scopeChanged) {
      // Exactly one key: send both, the unused one as null.
      body.category = v.scope === 'category' ? v.category : null;
      body.component_id = v.scope === 'component' ? v.component_id : null;
    }
    if (changed('customer_id')) body.customer_id = v.customer_id || null;
    if (changed('currency')) body.currency = v.currency;
    if (changed('effective_date')) body.effective_date = v.effective_date;
    if (changed('expiry_date')) body.expiry_date = v.expiry_date || null;
    if (changed('notes')) body.notes = v.notes.trim() || null;
    for (const k of MONEY_FIELDS) {
      if (!changed(k)) continue;
      const raw = v[k].trim();
      body[k] = raw === '' ? null : k === 'footage_rate' ? toStoredFootageRate(raw, units) : Number(raw);
    }
    // NEVER SENT WITH THE MODE OFF (the API answers DD_MODE_OFF to any of
    // them), and the ONE gate for that is the form: with the mode off these
    // inputs are not drawn, so their values cannot move, and only a value that
    // moved is sent. A second `if (daily)` here could never change the outcome
    // and so could never be tested (fe#1020 break-each-fix found it).
    for (const k of DAILY_MONEY_FIELDS) {
      if (!changed(k)) continue;
      const raw = v[k].trim();
      if (raw === '' && !editing) continue;
      body[k] = raw === '' ? null : Number(raw);
    }
    if (changed('standby_pct') && (editing || v.standby_pct.trim())) body.standby_pct = standbyPctFromInput(v.standby_pct) ?? null;
    if (editing && Object.keys(body).length === 0) {
      onClose();
      return;
    }
    if (!onlineOrSay(toast.error)) return;
    try {
      if (rate) await update.mutateAsync({ id: rate.id, body });
      else await create.mutateAsync(body);
      toast.success(editing ? 'Rate saved' : 'Rate added');
      onClose();
    } catch (error) {
      const data = (error as { response?: { data?: { code?: string; conflicting_rate_id?: string } } })?.response?.data;
      toast.error(getApiErrorMessage(error, editing ? 'The rate was not saved.' : 'The rate was not added.'));
      if (data?.code === 'RATE_OVERLAP' && data.conflicting_rate_id) onConflict(data.conflicting_rate_id);
    }
  };

  const toolOptions = tools.data?.data ?? [];
  const currentToolKnown = !v.component_id || toolOptions.some((t) => t.id === v.component_id);

  return (
    <DhDialog
      open
      wide
      title={editing ? 'Edit rate' : 'Add rate'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={PRIMARY} disabled={busy} onClick={submit}>
            {busy && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            {editing ? 'Save rate' : 'Add rate'}
          </button>
        </>
      }
    >
      <div className="space-y-5">
        <fieldset>
          <legend className="mb-2 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Prices</legend>
          <div className="mb-3 flex gap-5 text-sm text-fieldiq-text">
            <label className="inline-flex items-center gap-2">
              <input type="radio" name="rate-scope" checked={v.scope === 'category'} onChange={() => setV((a) => ({ ...a, scope: 'category' }))} />
              Every tool in a category
            </label>
            <label className="inline-flex items-center gap-2">
              <input type="radio" name="rate-scope" checked={v.scope === 'component'} onChange={() => setV((a) => ({ ...a, scope: 'component' }))} />
              One specific tool
            </label>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {v.scope === 'category' ? (
              <DhField label="Category *">
                <select className={DH_INPUT} value={v.category} onChange={set('category')}>
                  <option value="">Choose…</option>
                  {DH_COMPONENT_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {DH_CATEGORY_LABEL[c]}
                    </option>
                  ))}
                </select>
              </DhField>
            ) : (
              <>
                <DhField label="Find a tool">
                  <input className={DH_INPUT} value={toolSearch} onChange={(e) => setToolSearch(e.target.value)} placeholder="Serial or description" />
                </DhField>
                <DhField label="Tool *">
                  <select className={DH_INPUT} value={v.component_id} onChange={set('component_id')}>
                    <option value="">Choose…</option>
                    {!currentToolKnown && <option value={v.component_id}>{rate?.component_serial || 'Current tool'}</option>}
                    {toolOptions.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.serial_number} · {dhLabel(DH_CATEGORY_LABEL, t.category)}
                      </option>
                    ))}
                  </select>
                </DhField>
              </>
            )}
            <DhField label="Customer" hint="Leave as the company default to price every customer without a rate of their own.">
              <select className={DH_INPUT} value={v.customer_id} onChange={set('customer_id')}>
                <option value="">Company default</option>
                {rate?.customer_id && !customers.some((c) => c.id === rate.customer_id) && (
                  <option value={rate.customer_id}>{rate.customer_name || 'Current customer'}</option>
                )}
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </DhField>
          </div>
        </fieldset>

        <fieldset>
          <legend className="mb-2 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Figures</legend>
          <p className="mb-2 text-xs text-fieldiq-muted">Leave a figure blank if it is not quoted. A run billed on a figure that is not quoted cannot be invoiced.</p>
          <div className="grid gap-4 sm:grid-cols-3">
            {MONEY_FIELDS.map((k) => (
              <DhField key={k} label={`${moneyLabel(k, units)} (${v.currency})`}>
                <input className={DH_INPUT} inputMode="decimal" value={v[k]} onChange={set(k)} />
              </DhField>
            ))}
            <DhField label="Currency">
              <select className={DH_INPUT} value={v.currency} onChange={set('currency')}>
                <option value="CAD">CAD</option>
                <option value="USD">USD</option>
              </select>
            </DhField>
          </div>
        </fieldset>

        {daily && (
          <fieldset>
            <legend className="mb-2 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Daily billing (Directional &amp; MWD)</legend>
            <p className="mb-2 text-xs text-fieldiq-muted">
              Standby is a day rate or a share of the day rate, not both. Leave blank if not quoted.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <DhField label={`${DAILY_LABEL.standby_day_rate} (${v.currency})`}>
                <input className={DH_INPUT} inputMode="decimal" value={v.standby_day_rate} onChange={set('standby_day_rate')} aria-label="Standby day rate" />
              </DhField>
              <DhField label="Standby (% of day rate)">
                <input className={DH_INPUT} inputMode="decimal" value={v.standby_pct} onChange={set('standby_pct')} aria-label="Standby percent" />
              </DhField>
              <DhField label={`${DAILY_LABEL.mobilization_charge} (${v.currency})`}>
                <input className={DH_INPUT} inputMode="decimal" value={v.mobilization_charge} onChange={set('mobilization_charge')} aria-label="Mobilization charge" />
              </DhField>
              <DhField label={`${DAILY_LABEL.demobilization_charge} (${v.currency})`}>
                <input className={DH_INPUT} inputMode="decimal" value={v.demobilization_charge} onChange={set('demobilization_charge')} aria-label="Demobilization charge" />
              </DhField>
            </div>
          </fieldset>
        )}

        <fieldset>
          <legend className="mb-2 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">In effect</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            <DhField label="From *">
              <input type="date" className={DH_DATE_INPUT} value={v.effective_date} onChange={set('effective_date')} />
            </DhField>
            <DhField label="Until" hint="Blank keeps it in effect with no end date. Both days count.">
              <input type="date" className={DH_DATE_INPUT} value={v.expiry_date} onChange={set('expiry_date')} />
            </DhField>
          </div>
        </fieldset>

        <DhField label="Notes">
          <textarea className={`${DH_INPUT} h-20 py-2`} value={v.notes} onChange={set('notes')} maxLength={10000} />
        </DhField>
      </div>
    </DhDialog>
  );
}

function DeleteRateDialog({ rate, onClose }: { rate: DhRate; onClose: () => void }) {
  const remove = useDeleteRate();
  const submit = async () => {
    if (!onlineOrSay(toast.error)) return;
    try {
      await remove.mutateAsync(rate.id);
      toast.success('Rate deleted');
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'The rate was not deleted.'));
    }
  };
  return (
    <DhDialog
      open
      title="Delete this rate?"
      onClose={onClose}
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={DANGER} disabled={remove.isPending} onClick={submit}>
            Delete rate
          </button>
        </>
      }
    >
      <p className="text-sm text-fieldiq-text">
        {rate.component_id ? rate.component_serial : dhLabel(DH_CATEGORY_LABEL, rate.category)} for{' '}
        {rate.customer_id ? rate.customer_name : 'the company default'}, {dayRange(rate)}. Runs that are not invoiced yet stop using it.
        Invoices already made keep their prices. It can be restored with Show deleted.
      </p>
    </DhDialog>
  );
}
